import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { dbSurgeries, type ToothRecord } from '../../lib/localDB';
import { pricingDB, type PricingConfig } from '../../lib/pricingDB';
import { cariDB } from '../../lib/cariDB';
import Odontogram from './components/Odontogram';
import ToothPanel from './components/ToothPanel';
import VDKLegendModal from '../../components/feature/VDKLegendModal';
import {
  EMPTY_TOOTH, INITIAL_PATIENT_DATA,
  isToothComplete, buildVDK, isPatientComplete, canSaveRecord,
  PatientFormCard,
  type ToothData, type PatientFormData,
} from '../../shared/record';

const TODAY = new Date().toISOString().split('T')[0];

export default function NewRecord() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showProtocol, setShowProtocol] = useState(false);
  const [pricing, setPricing] = useState<PricingConfig>(() => pricingDB.get());

  useEffect(() => {
    pricingDB.load().then(setPricing).catch(() => setPricing(pricingDB.get()));
  }, []);

  const prefill = location.state?.prefillPatient;

  const [patientData, setPatientData] = useState<PatientFormData>(() => {
    if (prefill) {
      return {
        operation_date: TODAY,
        patient_name: prefill.patient_name || '',
        phone: prefill.phone || '',
        clinic: prefill.clinic || '',
        cinsiyet: prefill.cinsiyet || '',
        yas: prefill.yas || '',
        seker: prefill.seker || '',
        sigara: prefill.sigara || '',
      };
    }
    return {
      ...INITIAL_PATIENT_DATA,
      operation_date: TODAY,
    };
  });

  const [teethData, setTeethData] = useState<Record<number, ToothData>>({});
  const [selectedTeeth, setSelectedTeeth] = useState<number[]>([]);
  const [discountType, setDiscountType] = useState<'percent' | 'amount'>('percent');
  const [discountValue, setDiscountValue] = useState<string>('');

  const configuredTeeth = Object.keys(teethData)
    .map(Number)
    .filter((n) => isToothComplete(teethData[n]));

  const totalFee = configuredTeeth.reduce((sum, n) => {
    const t = teethData[n];
    return sum + pricingDB.calcToothFee(pricing, { soket: t.soket, greft: t.greft, kemik: t.kemik, hacim: t.hacim, healing: t.healing });
  }, 0);

  const discountNum = parseFloat(discountValue) || 0;
  const discountAmount = discountType === 'percent'
    ? Math.round((totalFee * Math.min(discountNum, 100)) / 100)
    : Math.min(discountNum, totalFee);
  const netFee = Math.max(totalFee - discountAmount, 0);

  const patientComplete = isPatientComplete(patientData);
  const canSave = canSaveRecord(patientData, configuredTeeth.length);

  const handlePatientChange = (key: keyof PatientFormData, value: string) => {
    setPatientData((prev) => ({ ...prev, [key]: value }));
  };

  const handleToothClick = (n: number) => {
    setSelectedTeeth((prev) => {
      if (prev.includes(n)) {
        return prev.filter((t) => t !== n);
      }
      return [...prev, n];
    });
    if (!teethData[n]) {
      setTeethData((prev) => ({ ...prev, [n]: { ...EMPTY_TOOTH } }));
    }
  };

  const handleToothChange = (updated: ToothData, teethNums?: number[]) => {
    const targets = teethNums ?? selectedTeeth;
    setTeethData((prev) => {
      const next = { ...prev };
      targets.forEach((n) => {
        next[n] = updated;
      });
      return next;
    });
  };

  const handleToothRemove = (n?: number) => {
    if (n !== undefined) {
      // Allow single remove from list view
      setTeethData((prev) => {
        const next = { ...prev };
        delete next[n];
        return next;
      });
      setSelectedTeeth((prev) => prev.filter((t) => t !== n));
    } else {
      // Complete removal from ToothPanel
      setTeethData((prev) => {
        const next = { ...prev };
        selectedTeeth.forEach((t) => delete next[t]);
        return next;
      });
      setSelectedTeeth([]);
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    setLoading(true);
    setError('');
    try {
      const teeth: ToothRecord[] = configuredTeeth.map((n) => {
        const t = teethData[n];
        return {
          tooth_number: n,
          kemik: t.kemik as '1' | '2' | '3',
          soket: t.soket as 'e' | 'h',
          greft: t.greft as 'e' | 'h',
          hacim: t.hacim as 'z' | 'y',
          healing: t.healing as 'e' | 'h',
          implant_brand: t.implant_brand,
          vdk: buildVDK(patientData, t),
        };
      });

      const saved = await dbSurgeries.insert({
        patient_name: patientData.patient_name.trim(),
        clinic: patientData.clinic.trim() || undefined,
        operation_date: patientData.operation_date,
        cinsiyet: patientData.cinsiyet as 'k' | 'e',
        yas: patientData.yas as '1' | '2' | '3' | '4',
        seker: patientData.seker as '0' | '1' | '2',
        sigara: patientData.sigara as '0' | '1' | '2',
        teeth,
      });

      // Her zaman cari kaydı oluştur — netFee=0 (tam indirim) olsa bile
      // Bu sayede dashboard ve edit-record indirimi geri okuyabiliyor
      await cariDB.upsertBySurgery(saved.id, {
        clinic_name: patientData.clinic.trim() || '-',
        type: 'borc',
        amount: netFee,
        date: patientData.operation_date,
        description: `Ameliyat — ${patientData.patient_name.trim()} (${configuredTeeth.length} diş)${discountAmount > 0 ? ` · ${pricing.currency}${discountAmount.toLocaleString('tr-TR')} indirim` : ''}`,
        surgery_id: saved.id,
      });

      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Kayıt sırasında bir hata oluştu';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const activeTooth = selectedTeeth.length > 0 ? (teethData[selectedTeeth[0]] ?? { ...EMPTY_TOOTH }) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
              <i className="ri-tooth-line text-lg text-white"></i>
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900">Yeni Kayıt</h1>
              <p className="text-xs text-gray-500">Odontogram ile vaka girişi</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setShowProtocol(true)}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all flex items-center whitespace-nowrap cursor-pointer text-sm font-medium border border-gray-100">
              Protokol Rehberi
            </button>
            <button type="button" onClick={() => navigate('/dashboard')}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm">
              <i className="ri-arrow-left-line"></i>Geri
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm flex items-center gap-2">
            <i className="ri-check-circle-line text-lg"></i>
            Kayıt oluşturuldu! Yönlendiriliyorsunuz...
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT COLUMN */}
          <div className="lg:col-span-2 space-y-5">
            <PatientFormCard
              patientData={patientData}
              onPatientChange={handlePatientChange}
              accentColor="teal"
              headerExtra={<VDKLegendModal variant="badge" />}
            />

            <Odontogram
              teethData={teethData}
              selectedTeeth={selectedTeeth}
              onToothClick={handleToothClick}
            />

            {/* Odontogram moved Summary from here to right column */}
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-5">
            {selectedTeeth.length > 0 && activeTooth !== null ? (() => {
              const snapshotTeeth = [...selectedTeeth];
              return (
                <ToothPanel
                  toothNumbers={snapshotTeeth}
                  data={activeTooth}
                  patientData={patientData}
                  onChange={(updated) => handleToothChange(updated, snapshotTeeth)}
                  onRemove={() => handleToothRemove()}
                  onClose={() => setSelectedTeeth([])}
                />
              );
            })() : (
              <div className="bg-white rounded-2xl border border-dashed border-teal-200 p-8 flex flex-col items-center justify-center text-center min-h-[200px] gap-3">
                <div className="w-14 h-14 bg-teal-50 rounded-xl flex items-center justify-center">
                  <i className="ri-tooth-line text-2xl text-teal-400"></i>
                </div>
                {configuredTeeth.length > 0 ? (
                  <>
                    <p className="text-sm font-bold text-teal-700">{configuredTeeth.length} diş girildi</p>
                    <p className="text-xs text-gray-400">Yeni grup seçmek için odontogramdan diş(ler)e tıklayın</p>
                    <p className="text-xs text-teal-600 font-medium bg-teal-50 px-3 py-1 rounded-full">Tüm dişler girildi mi? Aşağıdaki “Kaydet” butonuna basın ↓</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-gray-600">Diş seçilmedi</p>
                    <p className="text-xs text-gray-400">Odontogramdan bir veya birden fazla dişe tıklayarak parametrelerini girin</p>
                  </>
                )}
              </div>
            )}

            {/* CONFIGURED TEETH SUMMARY (MOVED HERE) */}
            {configuredTeeth.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-5 h-5 bg-teal-500 text-white rounded-full text-xs flex items-center justify-center font-bold">
                    {configuredTeeth.length}
                  </span>
                  Girilen Dişler
                </h3>
                <div className="space-y-2">
                  {configuredTeeth.sort((a, b) => a - b).map((n) => {
                    const t = teethData[n];
                    const vdk = buildVDK(patientData, t);
                    const fee = pricingDB.calcToothFee(pricing, { soket: t.soket, greft: t.greft, kemik: t.kemik, hacim: t.hacim, healing: t.healing });
                    return (
                      <div key={n} className="flex items-center justify-between py-2.5 px-4 bg-teal-50 rounded-xl hover:bg-teal-100 transition-colors cursor-pointer" onClick={() => handleToothClick(n)}>
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 bg-teal-500 text-white rounded-lg text-xs font-bold flex items-center justify-center">{n}</span>
                          <div className="flex gap-1.5 text-xs flex-wrap">
                            {[
                              { label: 'Kemik', val: t.kemik },
                              { label: 'Soket', val: t.soket },
                              { label: 'Greft', val: t.greft },
                              { label: 'Hacim', val: t.hacim },
                              { label: 'Healing', val: t.healing },
                            ].map((item) => (
                              <span key={item.label} className="px-2 py-0.5 bg-white text-teal-700 rounded font-medium border border-teal-200">
                                {item.label}: {item.val || '—'}
                              </span>
                            ))}
                            {t.implant_brand && (
                              <span className="px-2 py-0.5 bg-teal-500 text-white rounded font-medium flex items-center gap-1">
                                <i className="ri-award-line" style={{ fontSize: '10px' }}></i>
                                {t.implant_brand}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-sm font-bold text-teal-700 tracking-wider">{vdk}</span>
                          <span className="text-sm font-bold text-emerald-600 whitespace-nowrap">
                            {pricing.currency}{fee.toLocaleString('tr-TR')}
                          </span>
                          <button type="button" onClick={(e) => { e.stopPropagation(); handleToothRemove(n); }}
                            className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer">
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Total + Discount + Net */}
                <div className="mt-3 pt-3 border-t border-teal-100 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-600 flex items-center gap-1.5">
                      <i className="ri-coins-line text-teal-500"></i>
                      Toplam ({configuredTeeth.length} diş)
                    </span>
                    <span className={`text-base font-bold ${discountAmount > 0 ? 'text-gray-400 line-through text-sm' : 'text-teal-700'}`}>
                      {pricing.currency}{totalFee.toLocaleString('tr-TR')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-600 flex items-center gap-1.5 flex-shrink-0">
                      <i className="ri-percent-line text-orange-400"></i>İndirim
                    </span>
                    <div className="flex-1 flex items-center gap-1.5">
                      <div className="flex bg-gray-100 rounded-lg p-0.5">
                        <button type="button" onClick={() => { setDiscountType('percent'); setDiscountValue(''); }}
                          className={`px-2.5 py-1 rounded-md text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${discountType === 'percent' ? 'bg-orange-400 text-white' : 'text-gray-500 hover:text-gray-700'}`}>%</button>
                        <button type="button" onClick={() => { setDiscountType('amount'); setDiscountValue(''); }}
                          className={`px-2.5 py-1 rounded-md text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${discountType === 'amount' ? 'bg-orange-400 text-white' : 'text-gray-500 hover:text-gray-700'}`}>{pricing.currency}</button>
                      </div>
                      <input type="number" min="0" max={discountType === 'percent' ? 100 : totalFee} value={discountValue}
                        onChange={(e) => setDiscountValue(e.target.value)}
                        placeholder={discountType === 'percent' ? 'örn: 10' : 'örn: 500'}
                        className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-300 focus:border-transparent transition-all" />
                      {discountValue && (
                        <button type="button" onClick={() => setDiscountValue('')}
                          className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 flex-shrink-0 cursor-pointer">
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      )}
                    </div>
                    {discountAmount > 0 && (
                      <span className="text-sm font-bold text-orange-500 whitespace-nowrap flex-shrink-0">
                        -{pricing.currency}{discountAmount.toLocaleString('tr-TR')}
                      </span>
                    )}
                  </div>
                  <div className={`flex items-center justify-between pt-2 border-t ${discountAmount > 0 ? 'border-orange-100' : 'border-teal-100'}`}>
                    <span className={`text-sm font-bold flex items-center gap-1.5 ${discountAmount > 0 ? 'text-orange-600' : 'text-teal-700'}`}>
                      <i className={`${discountAmount > 0 ? 'ri-shield-check-line text-orange-400' : 'ri-coins-line text-teal-500'}`}></i>
                      {discountAmount > 0 ? 'Net Fiyat' : 'Toplam'}
                    </span>
                    <span className={`text-xl font-bold ${discountAmount > 0 ? 'text-orange-600' : 'text-teal-700'}`}>
                      {pricing.currency}{netFee.toLocaleString('tr-TR')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* SAVE CARD */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
              <h3 className="text-sm font-bold text-gray-800">Kaydet</h3>
              <div className="space-y-2 text-sm">
                {[
                  { label: 'Hasta adı', ok: patientData.patient_name.trim() !== '' },
                  { label: 'Cinsiyet & Yaş & Şeker & Sigara', ok: patientComplete },
                  { label: 'En az 1 diş girildi', ok: configuredTeeth.length > 0 },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-2">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${item.ok ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                      <i className={`${item.ok ? 'ri-check-line' : 'ri-subtract-line'} text-xs`}></i>
                    </span>
                    <span className={item.ok ? 'text-gray-800 font-medium' : 'text-gray-400'}>{item.label}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => navigate('/dashboard')}
                  className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer text-sm">İptal</button>
                <button type="button" onClick={handleSave} disabled={!canSave || loading}
                  className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all whitespace-nowrap ${canSave && !loading ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white hover:from-teal-600 hover:to-cyan-700 cursor-pointer' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
                  {loading ? 'Kaydediliyor...' : canSave ? `Kaydet (${configuredTeeth.length} diş)` : 'Eksik alan var'}
                </button>
              </div>
            </div>


          </div>
        </div>
      </div>

      {/* PROTOCOL POPUP MODAL */}
      {showProtocol && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setShowProtocol(false)}
        >
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div
            className="relative bg-white rounded-2xl w-full max-w-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-500 to-cyan-600 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-base">Tedavi Protokolü Zinciri</h2>
                <p className="text-teal-100 text-xs mt-0.5">6 aşamalı standart implant tedavi takibi</p>
              </div>
              <button
                type="button"
                onClick={() => setShowProtocol(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 text-white transition-all cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Steps */}
            <div className="p-6">
              {/* Chain visual */}
              <div className="flex flex-col gap-0">
                {[
                  { step: '1', label: 'Kontrol',       sub: 'Ameliyat sonrası +10 gün',      color: 'teal',    icon: 'ri-stethoscope-line',    desc: 'İlk yara kontrolü, dikiş durumu, iyileşme takibi.' },
                  { step: '2', label: '2. Ay Kontrol', sub: 'Ameliyat sonrası +60 gün',      color: 'cyan',    icon: 'ri-pulse-line',          desc: 'Osseointegrasyonun değerlendirilmesi, radyolojik kontrol.' },
                  { step: '3', label: 'Healing / Ölçü', sub: '+90 gün — Healing & İzlenim', color: 'emerald', icon: 'ri-rulers-line',          desc: 'Healing abutment takılması ve ölçü alınması.' },
                  { step: '4', label: 'Prova',         sub: '+7 gün — Yapı Provou',         color: 'amber',   icon: 'ri-search-eye-line',     desc: 'Kuron ya da köprü provası, uyum kontrolü.' },
                  { step: '5', label: 'Teslim',        sub: '+3 gün — Final Teslim',        color: 'orange',  icon: 'ri-gift-line',           desc: 'Protezin yapıştırılması / vida ile sabitlenmesi.' },
                  { step: '6', label: 'Final Kontrol', sub: '+2 gün — Son Kontrol',         color: 'green',   icon: 'ri-shield-check-line',   desc: 'Kapanış kontrolü, hasta memnuniyeti, tedavinin tamamlanması.' },
                ].map((item, idx, arr) => (
                  <div key={item.step} className="flex gap-4">
                    {/* Timeline spine */}
                    <div className="flex flex-col items-center">
                      <div className={`w-9 h-9 rounded-full bg-${item.color}-500 text-white flex items-center justify-center font-bold text-sm flex-shrink-0 shadow-sm`}>
                        {item.step}
                      </div>
                      {idx < arr.length - 1 && (
                        <div className="w-0.5 flex-1 bg-gray-200 my-1"></div>
                      )}
                    </div>
                    {/* Content */}
                    <div className={`flex-1 pb-${idx < arr.length - 1 ? '4' : '0'}`}>
                      <div className={`bg-${item.color}-50 border border-${item.color}-200 rounded-xl px-4 py-3 ${idx < arr.length - 1 ? 'mb-0' : ''}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <i className={`${item.icon} text-${item.color}-600 text-base`}></i>
                          <span className={`font-bold text-gray-900 text-sm`}>{item.label}</span>
                          <span className={`ml-auto text-xs font-medium text-${item.color}-600 bg-${item.color}-100 px-2 py-0.5 rounded-full`}>{item.sub.split('—')[0].trim()}</span>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Legend */}
              <div className="mt-5 pt-4 border-t border-gray-100 grid grid-cols-2 gap-2">
                {[
                  { color: 'gray',   icon: 'ri-time-line',           label: 'Bekliyor',     desc: 'Henüz zamanı gelmedi' },
                  { color: 'amber',  icon: 'ri-alarm-warning-line',   label: 'Bu Hafta',     desc: '7 gün içinde gelecek' },
                  { color: 'red',    icon: 'ri-error-warning-line',   label: 'Gecikmiş',     desc: 'Tarihi geçti' },
                  { color: 'green',  icon: 'ri-check-circle-line',    label: 'Tamamlandı',   desc: 'Adım yapıldı' },
                ].map((s) => (
                  <div key={s.label} className="flex items-center gap-2">
                    <i className={`${s.icon} text-${s.color}-500 text-base`}></i>
                    <div>
                      <p className="text-xs font-semibold text-gray-700">{s.label}</p>
                      <p className="text-xs text-gray-400">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
